<style>
.hero {
  @apply w-1/2 bg-cover hidden;
  background-image: var(--registration-hero-path);
}
.external-link {
  color: var(--primary);
}

@screen md {
  .hero {
    @apply block;
  }
}
</style>

<script>
import { onMount, tick } from "svelte"
import queryString from "query-string"
import { navigate, link } from "svelte-routing"

import { anonymousApi as API, handleErrors } from "@local/utils/api"
import {
  partner as _partner,
  partnerId as _partnerId,
  loginDisabled,
  loginPageTitle,
  loginPageSubTitle,
  externalLogin,
  externalLoginCTAUrl,
  externalLoginCTA,
} from "@local/store/theme"
import { auth } from "@local/store/auth"
import { isLoggedIn, isEmailConfirmed } from "@local/store/auth"

import Link from "@local/components/Link.svelte"
import FullLayout from "@shared/components/FullLayout.svelte"
import TextInput from "@shared/components/TextInput.svelte"
import Heading from "@local/components/Heading.svelte"
import SubHeading from "@local/components/SubHeading.svelte"
import Submit from "@local/components/Submit.svelte"
import ButtonLink from "@local/components/ButtonLink.svelte"
import { workflowLang } from "@shared/store/workflowKey"
import {
  emailText,
  passwordText,
  signInText,
  welcomeBackText,
  loginOrPasswordIncorrectText,
  forgotPasswordText,
  byUtilizingHelloMDServiceYouAgreeToTheText,
  termsOfServiceText,
  privacyPolicyText,
} from "@shared/utils/translations"

export let location = {}

$: subdomain = window.location.hostname.split(".")[0]

let queryParams
$: queryParams = queryString.parse(location.search)
$: next = queryParams.next

$: partnerId = $_partnerId
let form = {
  email: "",
  password: "",
}
let errors = {}

function hasText(value) {
  return value != null && String(value).trim() !== ""
}

async function submit() {
  try {
    const partnerPath = `partners/${partnerId}/`
    const tokens = await API.post(`${partnerPath}tokens`, {
      json: form,
      searchParams: { next },
    }).json()
    const { partnerOrigin } = auth(tokens)
    if (partnerOrigin !== subdomain) {
      window.location = `SVELTE_APP_PROTOCOL://${partnerOrigin}.SVELTE_APP_DOMAIN/login${location.search}`
      return
    }
    await tick()
    navigate(next || "/", { replace: true })
  } catch (err) {
    console.log({ err })
    let customErrMsgs = {
      email: {
        shouldBeValid: loginOrPasswordIncorrectText[$workflowLang],
      },
    }
    errors = await handleErrors({ err, customErrMsgs })
  }
}

onMount(async () => {
  if ($loginDisabled || ($isLoggedIn && $isEmailConfirmed)) {
    navigate(next || "/", { replace: true })
  }
})
</script>

<FullLayout>
  <div class="flex min-h-screen bg-white">
    <div class="hero"></div>
    <div class="md:w-1/2 max-w-xl w-full mx-auto my-24 px-4 py-5 shadow-none">
      <Heading
        content="{$loginPageTitle || welcomeBackText[$workflowLang]}"
        className="mb-12"
      />
      {#if $loginPageSubTitle}
        <SubHeading
          content="{$loginPageSubTitle}"
          className="text-align-center font-weight-normal font-size-lg mb-12"
        />
      {/if}
      {#if $externalLogin && hasText($externalLoginCTAUrl) && hasText($externalLoginCTA)}
        <ButtonLink
          url="{$externalLoginCTAUrl}"
          label="{$externalLoginCTA}"
          color="primary"
        />
      {:else}
        <form on:submit|preventDefault autocomplete="off" on:submit="{submit}">
          {#if errors.email}
            <div class="mb-6 text-center text-red-500">{errors.email[0]}</div>
          {/if}
          <TextInput
            name="email"
            label="{emailText[$workflowLang]}*"
            bind:value="{form.email}"
            class="mb-6"
          />
          <TextInput
            name="password"
            label="{passwordText[[$workflowLang]]}*"
            bind:value="{form.password}"
            type="password"
            class="mb-12"
          />
          <Submit
            label="{signInText[$workflowLang]}"
            color="primary"
            className="mb-6"
          />
          <div class="text-center">
            <Link
              href="/forgot-password{location.search}"
              class="font-size-normal"
            >
              {forgotPasswordText[$workflowLang]}
            </Link>
          </div>
          <div class="text-sm text-center mt-8">
            {byUtilizingHelloMDServiceYouAgreeToTheText[$workflowLang]}<a
              use:link
              href="/terms/terms-of-service"
              class="external-link">{termsOfServiceText[$workflowLang]}</a
            >
            &
            <a use:link href="/terms/privacy-policy" class="external-link"
              >{privacyPolicyText[$workflowLang]}</a
            >
          </div>
        </form>
      {/if}
    </div>
  </div>
</FullLayout>
